const IN_NAME_BRAND: unique symbol = Symbol("inNameAttributes");
const OTHER_BRAND: unique symbol = Symbol("otherAttributes");

export type TagAttributeType =
  | "text"
  | "groupType"
  | "date"
  | "person"
  | "tagType"
  | "number"
  | "integer"
  | "entityId"
  | "link"
  | "parentTag";

export type TagAttributeAction =
  | "optional"
  | "editable"
  | "readonly"
  | "mandatory";

export type TagAttributeGroup = "values" | "others" | "entityTag" | "parentTag";

export type TagConfigAttribute = {
  id: string;
  type: TagAttributeType;
  action: TagAttributeAction;
  label?: string | undefined;
  associatedValue?: unknown;
  defaultValue?: unknown;
  resolvedTeamTypeIds?: string[] | undefined;
  isDeleted?: boolean | undefined;
  isEntityTagAttr?: boolean | undefined;
};

/** Only `attributes.values` may produce this list. */
export type InNameAttributeList = readonly TagConfigAttribute[] & {
  readonly [IN_NAME_BRAND]: true;
};

export type OtherAttributeList = readonly TagConfigAttribute[] & {
  readonly [OTHER_BRAND]: true;
};

export type TagTypeAttributesConfig = {
  separator?: string | undefined;
  values?: TagConfigAttribute[] | undefined;
  others?: TagConfigAttribute[] | undefined;
  entityTag?: TagConfigAttribute[] | undefined;
  parentTag?: TagConfigAttribute[] | undefined;
};

const brand = <B extends symbol>(
  attrs: readonly TagConfigAttribute[],
  key: B
): readonly TagConfigAttribute[] & { readonly [K in B]: true } =>
  Object.assign([...attrs], { [key]: true as const });

export const inNameAttributes = (
  attributes: TagTypeAttributesConfig | null | undefined
): InNameAttributeList => brand(attributes?.values ?? [], IN_NAME_BRAND);

export const otherAttributes = (
  attributes: TagTypeAttributesConfig | null | undefined
): OtherAttributeList => brand(attributes?.others ?? [], OTHER_BRAND);

export const isInNameGroup = (group: TagAttributeGroup): group is "values" =>
  group === "values";

const assertNever = (value: never): never => {
  throw new Error(`Unhandled tag attribute variant: ${String(value)}`);
};

export const forAttributeGroup = <T>(
  group: TagAttributeGroup,
  handlers: {
    values: () => T;
    others: () => T;
    entityTag: () => T;
    parentTag: () => T;
  }
): T => {
  switch (group) {
    case "values":
      return handlers.values();
    case "others":
      return handlers.others();
    case "entityTag":
      return handlers.entityTag();
    case "parentTag":
      return handlers.parentTag();
    default:
      return assertNever(group);
  }
};
